import { createAsyncThunk } from '@reduxjs/toolkit';

import i18n from '@global/localization/i18n';
import { client } from '@global/Api';

import {
  GET_SUBSCRIPTION,
  GET_SUBSCRIPTION_RESPONSE,
  GET_SUBSCRIPTION_VARIABLES,
} from '@global/requests/subscriptionDetails';
import {
  B2CSubscriptionStatus,
  IProductParent,
  LOCALE_CODES,
  SubscriptionStatus,
} from '@global/types/types';
import { dateFormatter } from '@global/utils/date';
import { transformCardNumber } from '@global/utils/StringService';
import { getImgUrlByProduct, getOrderPrice } from '@global/utils/orderUtils';
import { subscriptionsByUid } from '../subscriptionSlice';
import { filterSubscription } from '@src/global/utils/filterSubscription';
import { getSubscriptionStatuses } from '@src/global/utils/getSubscriptionStatuses';
import { SiteConfig } from '@src/global/utils/SiteConfig';

interface GetSubscriptionsResult {
  [key: string]: subscriptionsByUid;
}

export const getSubscriptions = createAsyncThunk<
  GetSubscriptionsResult | null,
  { userUid: string },
  { rejectValue: string }
>('subscription/getSubscriptions', async ({ userUid }, { rejectWithValue }) => {
  try {
    const language = i18n.language as LOCALE_CODES;
    const statuses = getSubscriptionStatuses('subscription');

    const { data } = await client.query<
      GET_SUBSCRIPTION_RESPONSE,
      GET_SUBSCRIPTION_VARIABLES
    >({
      query: GET_SUBSCRIPTION,
      variables: {
        userUid,
        statuses,
      },
    });

    if (data.wGetSubscriptions.result.status === 'ERROR') {
      return rejectWithValue('SERVER_ERROR');
    }
    const subscriptions = data.wGetSubscriptions.result.data.edges.map(
      ({ node }) => node
    );

    const result: GetSubscriptionsResult = {};
    const { isB2CSite } = SiteConfig.getConfig();

    subscriptions.forEach((subscription) => {
      const payment = subscription.paybPayment;
      const address = subscription.paybBillingAddress;
      const address2 = address?.upaLine2 || '';
      const subsValidPeriod = subscription.subsValidPeriod;
      const paybNextBillDate = subscription.paybNextBillDate;
      const biSalable = subscription.hiConsistsOf[0]?.biiSalable;
      const hiPartOf = subscription.hiPartOf[0].hiPartOf[0];
      const order = isB2CSite ? hiPartOf : hiPartOf.order.edges[0].node;
      const orderId = order.biId;
      const subscriptionSiteId = order.wSiteId;

      result[subscription.uid] = {
        uid: subscription.uid || null,
        orderStatus: subscription.bpStatus.code,
        orderId,
        product: {
          maId: biSalable.maId,
          maName: biSalable.maName || biSalable.parents[0]?.node.maName,
          imageUrl: getImgUrlByProduct({
            imageUrl: biSalable.imageUrl,
            parents: biSalable.parents as IProductParent[],
          }),
        },
        lastBillBiId: subscription.lastBillBiId,
        orderPrice: getOrderPrice(subscription.paybPayment?.paymAmount.edges),
        periodInfo: {
          subscriptionBegan: dateFormatter(subsValidPeriod?.start, language),
          nextBillDate: dateFormatter(paybNextBillDate, language),
          subscriptionEndDate: dateFormatter(subsValidPeriod?.end, language),
        },
        paymentUid: payment?.uid,
        paymentInfo: {
          nameOnCard: payment?.ccpaNameOnCard,
          cardNumber: transformCardNumber(payment?.ccpaCardNumber),
          expirationDate: payment?.ccpaExpirationDate,
          wPaymentType: payment?.wPaymentType,
        },
        billingInfo: {
          name: payment?.ccpaNameOnCard || '',
          addressOne: address?.upaLine1 || '',
          addressTwo: address2,
          zip: address?.upaPostcode || '',
          city: address?.upaCity || '',
          state: address?.gadStateOrProvince || '',
          tel: subscription.wContactPhone || '',
          country: {
            name: address?.gadCountry.name || '',
            numericCode: address?.gadCountry.numericCode || '',
            iso2Code: address?.wCountry || address?.gadCountry.iso2Code || '',
          },
        },
        billingPlan:
          subscription.paybBillingPlan ||
          subscription.hiPartOf[0].paybBillingPlan,
        subscriptionSiteId,
        userEmail:
          subscription?.prRelatedParties?.edges[0]?.node?.paContacts?.edges?.find(
            (edge) => edge.node.typeName === 'EmailAddressContactMedium'
          )?.node?.email,
        isCancelled: [
          SubscriptionStatus.Cancelling,
          B2CSubscriptionStatus.PENDING_CANCELLATION,
          B2CSubscriptionStatus.CANCELLED,
        ].includes(subscription.bpStatus?.code),
      };
    });

    return filterSubscription(result);
  } catch (error) {
    console.error(error);
    return rejectWithValue('SERVER_ERROR');
  }
});
